import { getAuthToken } from './auth/tokenStorage';
import axios from 'axios';
import { API_URL } from '../config';
import { Contact } from '../screens/tasks/contacts/types';
import { contactLogger } from '../utils/logger';
import { redactError } from '../utils/redactError';

const API_BASE_URL = API_URL;

// Helper to get auth headers with JWT token
const getAuthHeaders = async () => {
  contactLogger.debug('=== FRONTEND CONTACT SERVICE: Getting auth token ===');
  const token = await getAuthToken();
  contactLogger.debug('Token found:', token ? 'YES' : 'NO');

  if (!token) {
    throw new Error('No authentication token found');
  }

  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  contactLogger.debug('=== FRONTEND CONTACT SERVICE: Auth headers created ===');
  return headers;
};

/**
 * Contact service.
 *
 * Historical note: this module used to fall back to `AsyncStorage` under the
 * `contacts_data` key on every network error — silently persisting names,
 * phone numbers, and emails on disk outside the vault boundary. That path
 * has been removed (see KNOWN_LIMITATIONS.md "Offline writes are no longer
 * supported for vault-managed secrets" + its follow-up note). Current
 * behavior: writes throw on network errors, reads return empty on network
 * errors. Matches the posture adopted in Step 4 for passwords / loyalty /
 * spouse credentials.
 *
 * Any residue left by the old code is cleared by `wipeSessionOnLogout`'s
 * LOGOUT_ASYNC_KEYS list, which includes `contacts_data` for belt-and-
 * braces cleanup.
 */
export const contactService = {
  // Store contact credentials
  async storeContact(contactData: Omit<Contact, 'id'>): Promise<string> {
    contactLogger.debug('=== FRONTEND CONTACT SERVICE: storeContact called ===');
    contactLogger.debug('API URL:', `${API_BASE_URL}/api/contacts`);
    contactLogger.debug('Contact data:', { ...contactData, email: contactData.email ? '[HIDDEN]' : undefined });

    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API_BASE_URL}/api/contacts`, contactData, { headers });
      contactLogger.debug('Response status:', response.status);

      if (response.status === 200 || response.status === 201) {
        contactLogger.debug('=== FRONTEND CONTACT SERVICE: storeContact succeeded ===');
        return response.data.contact.id;
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error: any) {
      contactLogger.error('=== FRONTEND CONTACT SERVICE: storeContact failed ===', redactError(error));

      if (error.response) {
        throw new Error(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
      }
      // Offline / unreachable / aborted: throw so the surrounding UI can
      // surface a retry affordance. We no longer persist contacts locally
      // on write failure — that path leaked PII onto disk outside the
      // vault. Callers should treat a thrown error as "user needs to
      // retry when online."
      throw new Error('Could not save contact: the server is unreachable. Please try again when you have a connection.');
    }
  },

  // Get contact by ID
  async getContact(contactId: string): Promise<Contact | null> {
    contactLogger.debug('=== FRONTEND CONTACT SERVICE: getContact called ===');
    contactLogger.debug('Contact ID:', contactId);

    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/api/contacts/${contactId}`, { headers });

      if (response.status === 200 && response.data.success) {
        contactLogger.debug('=== FRONTEND CONTACT SERVICE: getContact succeeded ===');
        return response.data.contact;
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error: any) {
      contactLogger.error('=== FRONTEND CONTACT SERVICE: getContact failed ===', redactError(error));

      if (error.response?.status === 404) {
        return null;
      }
      // Reads return null on any other failure — mirrors the other
      // vault-managed services' "empty on offline" read posture so the
      // surrounding screen can render without a crash.
      return null;
    }
  },

  // Get all contacts for a category
  async getAllContactsByCategory(category: string): Promise<Contact[]> {
    contactLogger.debug('=== FRONTEND CONTACT SERVICE: getAllContactsByCategory called ===');
    contactLogger.debug('Category:', category);

    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/api/contacts?category=${category}`, { headers });

      if (response.status === 200 && response.data.success) {
        contactLogger.debug('=== FRONTEND CONTACT SERVICE: getAllContactsByCategory succeeded ===');
        return response.data.contacts;
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error: any) {
      contactLogger.error('=== FRONTEND CONTACT SERVICE: getAllContactsByCategory failed ===', redactError(error));

      if (error.response?.status === 404) {
        return [];
      }
      return [];
    }
  },

  // Get all contacts
  async getAllContacts(): Promise<Contact[]> {
    contactLogger.debug('=== FRONTEND CONTACT SERVICE: getAllContacts called ===');

    try {
      const headers = await getAuthHeaders();

      // Add timeout to prevent infinite hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout (increased for AI operations)

      const response = await axios.get(`${API_BASE_URL}/api/contacts`, {
        headers,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.status === 200 && response.data.success) {
        contactLogger.debug('=== FRONTEND CONTACT SERVICE: getAllContacts succeeded ===');
        contactLogger.debug('Found contacts:', response.data.contacts.length);
        return response.data.contacts;
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error: any) {
      contactLogger.error('=== FRONTEND CONTACT SERVICE: getAllContacts failed ===', redactError(error));
      return [];
    }
  },

  // Update contact
  async updateContact(contactId: string, contactData: Partial<Contact>): Promise<Contact> {
    contactLogger.debug('=== FRONTEND CONTACT SERVICE: updateContact called ===');
    contactLogger.debug('Contact ID:', contactId);

    try {
      const headers = await getAuthHeaders();
      const response = await axios.put(`${API_BASE_URL}/api/contacts/${contactId}`, contactData, { headers });

      if (response.status === 200 && response.data.success) {
        contactLogger.debug('=== FRONTEND CONTACT SERVICE: updateContact succeeded ===');
        return response.data.contact;
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error: any) {
      contactLogger.error('=== FRONTEND CONTACT SERVICE: updateContact failed ===', redactError(error));

      if (error.response) {
        throw new Error(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
      }
      throw new Error('Could not update contact: the server is unreachable. Please try again when you have a connection.');
    }
  },

  // Delete contact
  async deleteContact(contactId: string): Promise<void> {
    contactLogger.debug('=== FRONTEND CONTACT SERVICE: deleteContact called ===');
    contactLogger.debug('Contact ID:', contactId);

    try {
      const headers = await getAuthHeaders();
      const response = await axios.delete(`${API_BASE_URL}/api/contacts/${contactId}`, { headers });

      if (response.status === 200 && response.data.success) {
        contactLogger.debug('=== FRONTEND CONTACT SERVICE: deleteContact succeeded ===');
        return;
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error: any) {
      contactLogger.error('=== FRONTEND CONTACT SERVICE: deleteContact failed ===', redactError(error));

      if (error.response) {
        throw new Error(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
      }
      throw new Error('Could not delete contact: the server is unreachable. Please try again when you have a connection.');
    }
  },
};
